import type { CertificateData } from '@/services/certificates.service'
import type { CertificateSettings } from '@/services/super-admin/certificate-settings.service'

/**
 * Lógica pura da pré-visualização do certificado de uma empresa.
 *
 * Espelha a cadeia de fallback de
 * `back/src/modules/certificates/certificates.service.ts`:
 * - Fundo: empresa.certificateBackgroundUrl ?? global.globalBackgroundUrl ?? null
 * - Assinatura da empresa: só com URL + nome do diretor
 * - Assinatura global: só com includeGlobalSignature E global URL + nome
 *
 * Mantida fora do componente pra ser testável sem React/React Query.
 * Aceita uma forma estrutural mínima de `company` pra ser reaproveitada
 * tanto pelo super_admin (Company com slug/active/etc) quanto pelo admin
 * local (MyCompany sem esses campos).
 */

export const MOCK_USER_NAME = 'Maria da Silva'
export const MOCK_COURSE_TITLE = 'Curso de Demonstração'

/**
 * Shape mínimo que o preview precisa ler. Tanto `Company` (super_admin)
 * quanto a Company do `/admin/companies/me` satisfazem estruturalmente.
 */
export interface CompanyCertificateView {
  name?: string
  certificateBackgroundUrl?: string | null
  certificateLogoUrl?: string | null
  certificateSignatureUrl?: string | null
  directorName?: string | null
  directorRole?: string | null
  includeGlobalSignature?: boolean
}

export interface CertificatePreview {
  /** Pacote pronto pro CertificateRenderer (com dados de aluno fictícios). */
  data: CertificateData
  /** Origem do fundo: "da empresa" | "global (FCamara)" | "não configurado". */
  bgLabel: string
  /** Origem das assinaturas: "empresa + global" | "da empresa" | "global (FCamara)" | "nenhuma". */
  sigLabel: string
  /** Avisos de config parcial que fazem algo não aparecer no certificado. */
  warnings: string[]
}

/**
 * Monta o pacote do certificado pra pré-visualização.
 *
 * O 3º arg `badges` é opcional: no preview do super_admin, o aluno
 * fictício "Maria da Silva" é tratado como tendo conquistado TODAS as
 * badges configuradas pela empresa — passamos a lista derivada de
 * `company.certificateBadges` (filtrada client-side pelas que têm
 * `iconUrl`). Se omitido, vira `[]` e a faixa não renderiza.
 */
export function buildCertificatePreview(
  company: CompanyCertificateView,
  globalSettings: CertificateSettings | undefined,
  badges: CertificateData['badges'] = []
): CertificatePreview {
  const backgroundUrl =
    company.certificateBackgroundUrl ?? globalSettings?.globalBackgroundUrl ?? null

  const signature: CertificateData['signature'] = {}
  if (company.certificateSignatureUrl && company.directorName) {
    signature.company = {
      url: company.certificateSignatureUrl,
      name: company.directorName,
      role: company.directorRole ?? null,
    }
  }
  if (
    company.includeGlobalSignature &&
    globalSettings?.globalSignatureUrl &&
    globalSettings?.globalDirectorName
  ) {
    signature.global = {
      url: globalSettings.globalSignatureUrl,
      name: globalSettings.globalDirectorName,
      role: globalSettings.globalDirectorRole ?? null,
    }
  }

  const data: CertificateData = {
    userName: MOCK_USER_NAME,
    courseTitle: MOCK_COURSE_TITLE,
    completedAt: new Date().toISOString(),
    backgroundUrl,
    companyLogoUrl: company.certificateLogoUrl ?? null,
    signature,
    badges,
  }

  const bgLabel = company.certificateBackgroundUrl
    ? 'da empresa'
    : globalSettings?.globalBackgroundUrl
      ? 'global (FCamara)'
      : 'não configurado'

  const hasCompanySig = !!signature.company
  const hasGlobalSig = !!signature.global
  const sigLabel =
    hasCompanySig && hasGlobalSig
      ? 'empresa + global'
      : hasCompanySig
        ? 'da empresa'
        : hasGlobalSig
          ? 'global (FCamara)'
          : 'nenhuma'

  const warnings: string[] = []
  if (company.certificateSignatureUrl && !company.directorName) {
    warnings.push(
      'A assinatura da empresa não aparece no certificado: falta preencher o nome do diretor.'
    )
  }
  if (company.directorName && !company.certificateSignatureUrl) {
    warnings.push(
      'O nome do diretor está preenchido, mas falta enviar a imagem da assinatura da empresa.'
    )
  }
  if (
    company.includeGlobalSignature &&
    (!globalSettings?.globalSignatureUrl || !globalSettings?.globalDirectorName)
  ) {
    warnings.push(
      '“Incluir assinatura global” está marcado, mas o template global não tem assinatura e nome cadastrados.'
    )
  }

  return { data, bgLabel, sigLabel, warnings }
}
